import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import {
  CertificationOrigin,
  CertificationStatusEnumV2,
  CertificationV2TypeEnum,
} from "certification/models/cti";
import {
  AlertController,
  Loading,
  LoadingController,
  ToastController,
  ViewController,
} from "ionic-angular";
import ProfessionalProvider from "models/resource-management/professional-provider";
import moment from "moment";
import { AuthService } from "../../../app/auth/auth.service";
import { InitialCtiForm } from "../../forms/initial.cti/initial.cti";
import { CertificationService } from "../../services/certification-service";

@Component({
  selector: "cti-confirm-page",
  templateUrl: "cti-confirm.page.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CtiConfirmPage implements AfterViewInit {
  @Output()
  public cancelClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  public submitClicked: EventEmitter<any> = new EventEmitter<any>();
  @Input()
  public username: string;
  @ViewChild("form")
  private ctiForm: InitialCtiForm;

  protected hasValidUsername = false;
  private latestWrittenCertificate: any = null;

  protected get doShowWrittenCti() {
    return this.viewCtrl.data.formType === "WrittenCtiForm";
  }

  protected get doShowVerbalCti() {
    return this.viewCtrl.data.formType === "VerbalCtiForm";
  }

  protected get hospiceInfo() {
    if (this.viewCtrl.data && this.viewCtrl.data.hospice) {
      return this.viewCtrl.data.hospice;
    }
    return null;
  }

  protected get selectedPatient() {
    if (this.viewCtrl.data && this.viewCtrl.data.patient) {
      return this.viewCtrl.data.patient;
    }
    return null;
  }

  protected get currentBenefitPeriod() {
    if (this.viewCtrl.data && this.viewCtrl.data.currentBenefitPeriod) {
      return this.viewCtrl.data.currentBenefitPeriod;
    }
    return null;
  }

  protected get currentResourceInfo(): ProfessionalProvider {
    if (this.viewCtrl.data && this.viewCtrl.data.currentResourceInfo) {
      return this.viewCtrl.data.currentResourceInfo;
    }
    return null;
  }

  protected get ctiv2() {
    if (this.viewCtrl.data && this.viewCtrl.data.ctiv2) {
      return this.viewCtrl.data.ctiv2;
    }
    return null;
  }

  constructor(
    private viewCtrl: ViewController,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
    private certificationSvc: CertificationService,
    private authSvc: AuthService
  ) {}

  ngAfterViewInit(): void {
    if (this.isIncludedInAModal) {
      this.renderer.addClass(
        this.elRef.nativeElement.parentNode,
        "modal-cti-confirmation"
      );
    }
  }

  get isIncludedInAModal() {
    return this.viewCtrl.data.showInAModal;
  }

  private onError(loading: Loading) {
    return (error) => {
      loading.onDidDismiss(() => {
        if (error.status !== 200) {
          let alert = this.alertCtrl.create({
            title: "Create New CTI Failed",
            message: error.message,
            buttons: ["OK"],
          });
          alert.present();
          this.submitClicked.emit({ newCertificationCreated: false });
        }
      });
      loading.dismiss();
    };
  }

  private onSuccessRecertification(loading: Loading) {
    return (result: any) => {
      if (result) {
        loading.onDidDismiss(() => {
          this.viewCtrl.onDidDismiss(() => {
            const successTotast = this.toastCtrl.create({
              message: "Recertify patient successfully",
              duration: 3.5 * 1000, //Default wait time, In case of error it will close after 2 min
              cssClass: "success",
            });
            successTotast.present();
          });
          if (this.viewCtrl.data.onModalDismissed) {
            this.viewCtrl.data.onModalDismissed(result);
          }
          this.viewCtrl.dismiss(result);
          this.submitClicked.emit({ newCertificationCreated: true });
        });
      }
      loading.dismiss();
    };
  }

  protected emitCancelClicked() {
    this.cancelClicked.emit();
    this.viewCtrl.dismiss();
  }

  protected emitSubmitClicked({
    narrativeStatement,
    signedByDate,
    signedBy,
    certificationPeriodFrom,
    certificationPeriodTo,
    attestation,
    patient,
  }) {
    patient = this.selectedPatient;
    const loading = this.loadingCtrl.create({
      content: "Saving certification info ...",
    });
    // This line has to be between the create() function of the loading and the call of present() function
    // Do not move this line out of this position

    // Converts data into the compatiable version with the service
    let payload;
    let signedDate = moment(new Date(signedByDate))
      .hour(new Date().getHours())
      .minute(new Date().getMinutes())
      .second(new Date().getSeconds())
      .toISOString();
    let patientInfo = {
      id: patient.id,
      patientChartId: patient.qbosId,
      firstName: patient.firstName,
      lastName: patient.lastName,
    };

    if (this.ctiv2) {
      let status: CertificationStatusEnumV2;
      if (this.currentBenefitPeriod.benefitPeriod == 1) {
        status = CertificationStatusEnumV2.NO; // If it's the initial then attending is required
      } else {
        status = CertificationStatusEnumV2.YES;
      }
      let signedByDate = moment().format("YYYY-MM-DD");
      let now = new Date();

      payload = {
        origin: CertificationOrigin.PATIENT_CHART,
        type: CertificationV2TypeEnum.WRITTEN_PATIENT_CHART_V2,
        certifyingPhysicianName: this.authSvc.getUser().name,
        certifyingPhysicianSignedDate: moment(new Date(signedByDate))
          .utc()
          .hour(now.getHours())
          .minute(now.getMinutes())
          .seconds(now.getSeconds())
          .toISOString(),
        certifyingObtainedFromId: this.currentResourceInfo
          ? this.currentResourceInfo.id
          : null,
        certifyingObtainedFromQbosId: this.currentResourceInfo
          ? this.currentResourceInfo.externalId
          : null,
        patient: patientInfo,
        narrativeStatement: narrativeStatement,
        hospice: this.hospiceInfo,
        benefitPeriod: this.currentBenefitPeriod.id,
        patientQbosId: patientInfo.patientChartId,
        status,
        narrativeOnFile: false,
        attendingSignature: 1,
        certifyingSignature: 1,
        patientAdmitDate: patient.patientChart.admitDate,
      };
    } else {
      payload = {
        referred: true,
        attestation,
        origin: "IDG",
        briefStatement: narrativeStatement,
        certificationDateTime: moment(new Date(signedByDate))
          .hour(new Date().getHours())
          .minute(new Date().getMinutes())
          .second(new Date().getSeconds())
          .toISOString(),
        physicianSignature: signedBy,
        staffSignature: "testing",
        type: "WRITTEN_IDG",
        periodEndDateTime: moment
          .utc(certificationPeriodTo)
          .hour(23)
          .minute(0)
          .second(0)
          .toISOString(),
        periodStartDateTime: moment
          .utc(certificationPeriodFrom)
          .hour(0)
          .minute(0)
          .second(0)
          .toISOString(),
        hospice: this.hospiceInfo,
        patient: patientInfo,
      };
    }
    loading.present();

    // Retrieve written CTI's to check if it nees to create a new one or only to uppdate the latest one
    this.certificationSvc
      .getWrittenCertificationListV2(patientInfo.patientChartId)
      .subscribe((response: any) => {
        let certifications = response["content"];

        certifications = certifications.filter(
          (c) => c.benefitPeriod == this.currentBenefitPeriod.benefitPeriod
        );

        certifications.sort((c1, c2) => {
          if (c1.id > c2.id) {
            return 1;
          } else if (c1.id < c2.id) {
            return -1;
          }
          return 0;
        });

        if (certifications.length > 0) {
          this.latestWrittenCertificate =
            certifications[certifications.length - 1];
        }
        if (this.ctiv2) {
          if (this.latestWrittenCertificate != null) {
            // Check the origin of the certificate to decide wether update patient-chart or referral
            let origin = this.latestWrittenCertificate.origin;

            // If there's an existing written certificate, update it's brief narrative statement
            let signedByDate = moment().format("YYYY-MM-DD");
            let now = new Date();

            payload = {
              id: this.latestWrittenCertificate.id,
              benefitPeriod: this.latestWrittenCertificate.benefitPeriodId,
              narrativeStatement: payload.narrativeStatement,
              certifyingPhysicianName: this.authSvc.getUser().name,
              certifyingObtainedFromQbosId: this.currentResourceInfo
                ? this.currentResourceInfo.externalId
                : this.latestWrittenCertificate.certifyingObtainedFromQbosId,
              certifyingObtainedFromId: this.currentResourceInfo
                ? this.currentResourceInfo.id
                : this.latestWrittenCertificate.certifyingObtainedFromId,
              certifyingPhysicianSignedDate: moment(new Date(signedByDate))
                .utc()
                .hour(now.getHours())
                .minute(now.getMinutes())
                .seconds(now.getSeconds())
                .toISOString(),
              certifyingObtainedOn:
                this.latestWrittenCertificate.certifyingReceivedDate,
              narrativeOnFile: false,
              reasonForChange: "",
              type: this.latestWrittenCertificate.type,
              status: this.latestWrittenCertificate.status,
              origin: this.latestWrittenCertificate.origin,
              patientQbosId: patientInfo.patientChartId,
              hospice: this.hospiceInfo,
              attendingSignature: 0,
              certifyingSignature: 1,
            };

            if (this.ctiv2 && this.latestWrittenCertificate.id > 0) {
              payload.attendingObtainedFromId = null;
              payload.attendingObtainedFromQbosId = null;
              payload.attendingPhysicianName = "";
              payload.attendingPhysicianSignedDate = null;
              payload.patientAdmitDate = patient.patientChart.admitDate;
            }

            switch (origin) {
              case CertificationOrigin.REFERRAL:
                this.certificationSvc
                  .Referral_UpdateWrittenCtiv2(payload)
                  .subscribe(
                    this.onSuccessRecertification(loading),
                    this.onError(loading)
                  );
                break;
              case CertificationOrigin.PATIENT_CHART:
                this.certificationSvc
                  .PatientChart_UpdateWrittenCtiv2(payload)
                  .subscribe(
                    this.onSuccessRecertification(loading),
                    this.onError(loading)
                  );
                break;
            }
          } else {
            // If there's no written certifications by now, create a new CTI with it's corresponding narrative statement
            this.certificationSvc
              .PatientChart_CreateWrittenCTIv2(payload)
              .subscribe(
                this.onSuccessRecertification(loading),
                this.onError(loading)
              );
          }
        } else {
          this.certificationSvc
            .IDG_createWrittenCti(payload)
            .subscribe(
              this.onSuccessRecertification(loading),
              this.onError(loading)
            );
        }
      });
  }

  protected get currentUserFullName() {
    return this.viewCtrl.data.username;
  }

  protected onValueChanged(value) {
    this.hasValidUsername = value.signedBy === this.currentUserFullName;
  }
}



// WEBPACK FOOTER //
// ./src/certification/pages/cti-confirmation/cti-confirm.page.ts